<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { PrimaryButton, router, RawSfError, dateHelpers, LocationSelector } from '@hfc/ui-core';
import { EffectiveDatesResponse } from '@/application/shared/domain/EffectiveDatesResponse';
import { Person } from '@/quote/domain/Person';
import PersonForm from '@/quote/infrastructure/components/PersonForm.vue';
import EffectiveDateSelector from '@/quote/infrastructure/components/EffectiveDateSelector.vue';
import AddPersonButton from '@/quote/infrastructure/components/AddPersonButton.vue';
import { useForm } from 'vee-validate';
import { AppTypeDentalOrVision } from '@/application/shared/domain/DentalAndVisionApplication';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { useUrlHelper } from '@/shared/infrastructure/composition/useUrlHelper';
import { useHandleApiError } from '@/shared/infrastructure/composition/useHandleApiError';
import { AxiosError } from 'axios';
import { useAppStore } from '@/shared/infrastructure/composition/useAppStore';
import { useCheckCoverage } from '@/quote/infrastructure/composition/useCheckCoverage';
import { useQuoteStore } from '@/quote/infrastructure/composition/useQuoteStore';
import { usePlanStore } from '@/planList/infrastructure/composition/usePlanStore';
import { useDentalAndVisionApplicationStore } from '@/application/shared/infrastructure/composition/useDentalAndVisionApplicationStore';

const { appStore } = useAppStore();
const { checkCoverage } = useCheckCoverage();
const { quoteStore, quoteState } = useQuoteStore();
const { planStore } = usePlanStore();
const { dentalAndVisionApplicationStore } = useDentalAndVisionApplicationStore();
const { isSeniorPage } = useUrlHelper();
const { isAdult } = dateHelpers();

const props = withDefaults(defineProps<{
  applicationType: AppTypeDentalOrVision;
  titlePrefix?: string;
}>(), {
  titlePrefix: ''
});

const planListRouteName = `${props.applicationType.charAt(0).toUpperCase() + props.applicationType.slice(1)}PlanList`;
const locationSelectorRef = ref();
const state = computed(() => quoteState.value[props.applicationType]);
const applicationTypeIcon = computed(() => props.applicationType === APPLICATION_TYPES.DENTAL ? 'tooth' : 'eye');
const showNonPrimaryApplicants = computed(() => state.value?.primary?.date_of_birth ? isAdult(state.value.primary.date_of_birth) : true);

const effectiveDateOptions = ref<Array<EffectiveDatesResponse>>([]);

const { handleSubmit } = useForm();
const { handleSfApiError, handleServicesApiError } = useHandleApiError();
const runValidations = handleSubmit(() => true);

const submitData = async (): Promise<void> => {
  const valid = await runValidations();
  const locationValid = await locationSelectorRef.value?.isValidLocation();

  if (valid && locationValid) {
    planStore.resetLocalStorage(props.applicationType);
    dentalAndVisionApplicationStore.resetLocalStorage(props.applicationType);
    await quoteStore.submitQuote(props.applicationType);
    router.push({ name: planListRouteName });
  }
};

const handleError = (error: AxiosError<RawSfError>): void => {
  handleSfApiError(error);
};

const handlePrimaryApplicantUpdate = (val: Person): void => {
  const isPrimaryApplicantAdult = val.date_of_birth ? isAdult(val.date_of_birth) : true;
  if (!isPrimaryApplicantAdult) {
    quoteStore.removeNonPrimaryApplicantApplicants(props.applicationType);
  }
  quoteStore.updatePerson(val, 0, props.applicationType);
};

const setEffectiveDateOptions = async (): Promise<void> => {
  const result = await appStore.getEffectiveDates(props.applicationType);
  if (result.error) {
    handleSfApiError(result.error);
  }
  if (result.success && result.data) {
    effectiveDateOptions.value = result.data;
  }
};

onMounted(async () => {
  await quoteStore.updateQuote({ isSenior: isSeniorPage() }, props.applicationType);

  const { error } = await quoteStore.getQuoteInfo(props.applicationType);

  if (error) {
    handleServicesApiError(error);
  }
  await setEffectiveDateOptions();
});

onBeforeUnmount(() => {
  appStore.updateCustomTitle('');
});

</script>
<template>
  <h3 class="text-3xl font-medium py-5">Find <span class="capitalize">{{titlePrefix}} {{ applicationType }} plans </span></h3>
  <form class="flex flex-row bg-white rounded-md mx-2 md:mx-auto mb-4 md:mb-10"  @submit.prevent="submitData">
    <div class="hidden md:flex flex-col justify-center items-center border-r-4 border-r-blue-200 max-w-[285px] space-y-20">
      <carrier-logo class="w-56 lg:w-72 px-6 flex items-center justify-center" carrier-id="blue_shield" vertical="true" no-link="true" :coverage-type="props.applicationType" />
      <hfc-icon :icon="applicationTypeIcon" class="h-20 text-blue-600" />
    </div>
    <div class="flex flex-grow flex-col px-10 py-8 space-y-4 lg:min-w-[530px]">
      <div class="flex flex-grow flex-col md:flex-row md:space-x-4">
        <div class="flex-1 flex-grow">
          <PersonForm
            v-if="state.primary"
            :show-separator="false"
            :person="state.primary"
            :index="0"
            :show-index="false"
            :show-require-coverage="false"
            date-of-birth-label="Birth Date"
            @updatePerson="handlePrimaryApplicantUpdate" />
        </div>
        <div class="flex-1 flex-grow md:mt-16">
          <LocationSelector
            v-if="!quoteState.loadingQuote"
            ref="locationSelectorRef"
            class="space-x-0 sm:space-x-4 space-y-4 sm:space-y-0"
            custom-zip-class="h-16"
            custom-county-class="h-16"
            :zipcode="state.zip_code"
            :county="state.county"
            :application-type="applicationType"
            :validate-coverage="true"
            :check-coverage="checkCoverage"
            @handleError="handleError"
            @update:zip-code="quoteStore.updateQuoteInfo({ zip_code: $event }, applicationType)"
            @update:county="quoteStore.updateQuoteInfo({ county: $event }, applicationType)" />
        </div>
      </div>
      <div class="flex flex-col sm:flex-row justify-start items-start space-y-2 sm:space-y-0 sm:space-x-4">
        <EffectiveDateSelector
          v-if="effectiveDateOptions.length > 0"
          :effective-date="state.effective_date"
          :options="effectiveDateOptions"
          @update:effectiveDate="effectiveDate => quoteStore.updateQuoteInfo({ effective_date: effectiveDate }, applicationType)"/>
      </div>

      <div v-if="!showNonPrimaryApplicants" class="text-red">
        Note: For child-only applications, you apply separately for each child.
      </div>

      <div v-else class="flex flex-col sm:flex-row justify-start items-start space-y-2 sm:space-y-0 sm:space-x-4">
        <AddPersonButton
          v-if="quoteStore.canAddSpouse(applicationType)"
          label="Add Spouse"
          icon="plus"
          @onClick="quoteStore.addSpouse(applicationType)">
        </AddPersonButton>
        <AddPersonButton
          v-if="quoteStore.canAddChildren(applicationType)"
          label="Add Child"
          icon="plus"
          @onClick="quoteStore.addChild(applicationType)">
        </AddPersonButton>
        <AddPersonButton
          v-if="quoteStore.canAddDependents(applicationType)"
          label="Add Dependent"
          icon="plus"
          @onClick="quoteStore.addDependent(applicationType)">
        </AddPersonButton>
      </div>

      <div class="">
        <PersonForm
          v-if="state.spouse"
          :person="state.spouse"
          :index="0"
          :show-index="false"
          :min-age="18"
          :show-require-coverage="false"
          date-of-birth-label="Date of Birth"
          @remove="quoteStore.removeSpouse(applicationType)"
          @updatePerson="(val: Person) => quoteStore.updatePerson(val, 0, applicationType)" />

        <PersonForm
          v-for="(child, index) in state.children"
          :key="index"
          :person="child"
          :index="index"
          :show-index="true"
          :min-age="0"
          :show-require-coverage="false"
          date-of-birth-label="Date of Birth"
          @remove="quoteStore.removeChildren(index, applicationType)"
          @updatePerson="(val: Person) => quoteStore.updatePerson(val, index, applicationType)" />

        <PersonForm
          v-for="(dependant, index) in state.dependants"
          :key="index"
          :person="dependant"
          :index="index"
          :show-index="true"
          :min-age="0"
          :show-require-coverage="false"
          date-of-birth-label="Date of Birth"
          @remove="quoteStore.removeDependents(index, applicationType)"
          @updatePerson="(val: Person) => quoteStore.updatePerson(val, index, applicationType)" />
      </div>

      <div class="flex justify-center">
        <PrimaryButton :is-loading="quoteState.loadingQuote" @click.prevent="submitData" size="xl">
          Get Quotes Online
        </PrimaryButton>
      </div>
    </div>
  </form>
</template>
